import React from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getPostBySlug } from '../utils';

function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : null;

  if (!post) {
    return (
      <div className="container mx-auto px-4 text-2xl font-display text-slate-800/90">
        Post not found
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
      <article className="glass-card rounded-[1.75rem] p-4 sm:p-8 ring-1 ring-slate-300/35">
        <h1 className="font-display text-3xl sm:text-4xl font-semibold mb-4 aero-title-gradient-dark">
          {post.frontmatter.title}
        </h1>
        <div className="text-slate-600 mb-8 text-sm sm:text-base font-light border-b border-slate-300/40 pb-6">
          <p>
            Published on{' '}
            {new Date(post.frontmatter.date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h2: ({ node, children, ...props }) => (
              <h2
                className="font-display text-2xl sm:text-3xl font-medium text-slate-900 mt-10 mb-4 border-b border-slate-300/50 pb-2"
                {...props}
              >
                {children}
              </h2>
            ),
            p: ({ node, ...props }) => (
              <p className="text-slate-800/95 mb-4 leading-relaxed" {...props} />
            ),
            code: ({ node, className, children, ...props }) => (
              <code
                className={`rounded-md border border-white/20 bg-sky-950/45 px-2 py-0.5 font-mono text-sm text-cyan-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.15)] ${className || ''}`}
                {...props}
              >
                {children}
              </code>
            ),
            pre: ({ node, ...props }) => (
              <pre
                className="my-4 overflow-x-auto rounded-xl border border-white/25 bg-gradient-to-b from-sky-950/80 to-slate-950/90 p-4 font-mono text-sm text-sky-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_12px_40px_rgba(0,40,90,0.45)] backdrop-blur-sm"
                {...props}
              />
            ),
            a: ({ node, children, ...props }) => (
              <a
                className="text-sky-700 underline decoration-sky-400/60 underline-offset-2 transition-colors duration-300 hover:text-sky-900 hover:decoration-sky-700/80"
                {...props}
              >
                {children}
              </a>
            ),
            ul: ({ node, ...props }) => (
              <ul className="list-disc list-inside text-slate-800/95 mb-4 marker:text-sky-600" {...props} />
            ),
            li: ({ node, ...props }) => <li className="mb-2" {...props} />,
            blockquote: ({ node, ...props }) => (
              <blockquote
                className="border-l-4 border-sky-400/80 pl-4 my-4 italic text-slate-700 bg-sky-100/35 rounded-r-lg py-2"
                {...props}
              />
            ),
          }}
        >
          {post.content}
        </ReactMarkdown>
      </article>
    </div>
  );
}

export default BlogPostPage;
